/**
 * Hooks local-first de CLASES EN VIVO (F3-G). La sesión y el roster llegan por
 * los streams por rol (my_cohorts_student/teacher): el alumno ve su sesión; el
 * profesor ve además el roster de la cohorte. El acceso real a la videollamada
 * lo autoriza el servidor (`/api/live/.../join`).
 */
import type { Accessor } from "solid-js";
import {
  cohortQueryKeys,
  getCohortQuery,
  getSessionByIdQuery,
  scheduleQueryKeys,
} from "@space/shared";
import { createPowerSyncQuery } from "@space/shared/solid";
import type { ClassSession, Cohort } from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  return { db, drizzle, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/** Una sesión de clase por id (columnas sincronizadas). */
export function useLiveSession(sessionId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<ClassSession>({
    queryKey: () => scheduleQueryKeys.sessionById(sessionId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = sessionId();
      return db && id ? getSessionByIdQuery(db, id) : null;
    },
    enabled: () => Boolean(sessionId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Una cohorte por id (para nombre/curso). */
export function useCohort(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Cohort>({
    queryKey: () => cohortQueryKeys.byId(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

// D3 (plan 06 · E3): el `useCohortRoster` que vivía aquí era una COPIA literal
// del de `features/academy/hooks/use-cohort-data` —misma consulta, misma clave
// de caché— y la pantalla de clase en vivo lo importa ahora de allí. Una
// consulta, un hook: dos hooks sobre la misma clave se desincronizan en cuanto
// uno de los dos cambia de fuente, que es justo lo que esta etapa hace.
